
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

export type ProductVariant = Database['public']['Tables']['product_variants']['Row'];
export type ProductVariantInsert = Database['public']['Tables']['product_variants']['Insert'];
export type ProductVariantUpdate = Database['public']['Tables']['product_variants']['Update'];

export const fetchProductVariants = async (productId: string): Promise<ProductVariant[]> => {
  const { data, error } = await supabase
    .from("product_variants")
    .select("*")
    .eq("product_id", productId)
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);
  return data || [];
};

export const addProductVariant = async (variant: ProductVariantInsert): Promise<ProductVariant> => {
  const { data, error } = await supabase
    .from("product_variants")
    .insert(variant)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const updateProductVariant = async (id: string, variant: ProductVariantUpdate): Promise<ProductVariant> => {
  const { data, error } = await supabase
    .from("product_variants")
    .update(variant)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const deleteProductVariant = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("product_variants")
    .delete()
    .eq("id", id);

  if (error) throw new Error(error.message);
};

export const setDefaultVariant = async (productId: string, variantId: string): Promise<void> => {
  // First, set all variants of this product to not default
  const { error: updateError } = await supabase
    .from("product_variants")
    .update({ is_default: false })
    .eq("product_id", productId);

  if (updateError) throw new Error(updateError.message);

  // Then set the selected variant as default
  const { error: setDefaultError } = await supabase
    .from("product_variants")
    .update({ is_default: true })
    .eq("id", variantId);

  if (setDefaultError) throw new Error(setDefaultError.message);
};

export const cleanupVariantsNotInAttributes = async (
  productId: string
): Promise<{ deleted: number; checked: number; prevented: boolean }> => {
  // Validate productId
  if (!productId || productId === 'temp') {
    return { deleted: 0, checked: 0, prevented: true };
  }

  // Load attributes for this product
  const { data: attrs, error: attrsError } = await supabase
    .from('product_attributes')
    .select('id, name')
    .eq('product_id', productId)
    .in('name', ['pot_color', 'pot_type', 'pot_size']);

  if (attrsError) throw new Error(attrsError.message);

  const idByName = Object.fromEntries((attrs || []).map((a: any) => [a.name, a.id]));

  const attrIds = Object.values(idByName).filter(Boolean) as string[];

  const allowed = {
    pot_color: new Set<string>(),
    pot_type: new Set<string>(),
    pot_size: new Set<string>(),
  };

  if (attrIds.length > 0) {
    const { data: values, error: valsError } = await supabase
      .from('attribute_values')
      .select('name, attribute_id, enabled')
      .in('attribute_id', attrIds)
      .eq('enabled', true);

    if (valsError) throw new Error(valsError.message);

    for (const v of values || []) {
      const entry = Object.entries(idByName).find(([_, id]) => id === (v as any).attribute_id);
      const key = entry?.[0] as 'pot_color' | 'pot_type' | 'pot_size' | undefined;
      if (key && (v as any).name) {
        allowed[key].add((v as any).name as string);
      }
    }
  }

  // Fetch all variants for this product
  const { data: variants, error: varError } = await supabase
    .from('product_variants')
    .select('id, pot_type, size, pot_color')
    .eq('product_id', productId);

  if (varError) throw new Error(varError.message);

  const toDelete: string[] = [];
  for (const v of variants || []) {
    const potColor = (v as any).pot_color as string | null;
    const potType = (v as any).pot_type as string | null;
    const size = (v as any).size as string | null;

    const badColor = allowed.pot_color.size > 0 && potColor !== null && !allowed.pot_color.has(potColor);
    const badType = allowed.pot_type.size > 0 && potType !== null && !allowed.pot_type.has(potType);
    const badSize = allowed.pot_size.size > 0 && size !== null && !allowed.pot_size.has(size);

    if (badColor || badType || badSize) {
      toDelete.push((v as any).id as string);
    }
  }

  if (toDelete.length === 0) {
    return { deleted: 0, checked: (variants || []).length, prevented: false };
  }

  // Prevent deleting ALL variants - safety check
  if (toDelete.length === (variants || []).length) {
    console.warn('Cleanup would delete ALL variants - operation prevented');
    return { deleted: 0, checked: (variants || []).length, prevented: true };
  }

  const { data: deletedRows, error: delError } = await supabase
    .from('product_variants')
    .delete()
    .in('id', toDelete)
    .select('id');

  if (delError) throw new Error(delError.message);

  return { deleted: deletedRows?.length || 0, checked: (variants || []).length, prevented: false };
};

export const generateVariantsFromAttributes = async (
  productId: string
): Promise<{ created: number; skipped: number; updated: number; error?: string }> => {
  // Validate productId
  if (!productId || productId === 'temp') {
    return { created: 0, skipped: 0, updated: 0, error: 'Invalid product ID' };
  }

  // Fetch base product data for defaults and dependency rules
  const { data: product, error: prodError } = await supabase
    .from('products')
    .select('price, stock, dependency_rules')
    .eq('id', productId)
    .single();
  if (prodError) throw new Error(prodError.message);

  // Parse dependency rules
  const dependencyRules = Array.isArray((product as any)?.dependency_rules) 
    ? (product as any).dependency_rules as Array<{
        pot_size: string;
        allowed_pot_types: string[];
        allowed_pot_colors: string[];
        type_color_rules?: Array<{ pot_type: string; allowed_colors: string[] }>;
      }>
    : [];

  // Get attribute IDs for pot_type, pot_size, pot_color
  const { data: attrs, error: attrsError } = await supabase
    .from('product_attributes')
    .select('id, name')
    .eq('product_id', productId)
    .in('name', ['pot_color', 'pot_type', 'pot_size']);
  if (attrsError) throw new Error(attrsError.message);

  const idByName = Object.fromEntries((attrs || []).map((a: any) => [a.name, a.id]));
  const potTypeId = idByName['pot_type'];
  const potSizeId = idByName['pot_size'];
  const potColorId = idByName['pot_color'];

  // pot_type and pot_size are required for variants
  if (!potTypeId || !potSizeId) {
    return { created: 0, skipped: 0, updated: 0 };
  }

  const attrIdsToLoad = [potTypeId, potSizeId, potColorId].filter(Boolean) as string[];
  const { data: values, error: valsError } = await supabase
    .from('attribute_values')
    .select('name, attribute_id, enabled')
    .in('attribute_id', attrIdsToLoad)
    .eq('enabled', true)
    .order('position', { ascending: true });
  if (valsError) throw new Error(valsError.message);

  const byAttr = new Map<string, string[]>();
  byAttr.set(potTypeId, []);
  byAttr.set(potSizeId, []);
  if (potColorId) byAttr.set(potColorId, []);

  for (const v of values || []) {
    const attrId = (v as any).attribute_id as string;
    const name = (v as any).name as string;
    if (!byAttr.has(attrId)) continue;
    byAttr.get(attrId)!.push(name);
  }

  const potTypes = byAttr.get(potTypeId) || [];
  const sizes = byAttr.get(potSizeId) || [];
  const colors = potColorId ? (byAttr.get(potColorId) || []) : [];

  // Must have at least one type and size
  if (potTypes.length === 0 || sizes.length === 0) {
    return { created: 0, skipped: 0, updated: 0, error: 'No enabled attributes found' };
  }

  // Helper function to check if a combination is allowed by dependency rules
  const isCombinationAllowed = (potType: string, potSize: string, potColor: string | null): boolean => {
    if (dependencyRules.length === 0) return true; // No rules = all allowed
    const rule = dependencyRules.find(r => r.pot_size === potSize);
    if (!rule) return false; // Rules exist but none for this size = not allowed
    
    // Check pot type is allowed for this size
    if (rule.allowed_pot_types.length > 0 && !rule.allowed_pot_types.includes(potType)) {
      return false;
    }
    
    // Check color is allowed at size level
    if (potColor && rule.allowed_pot_colors.length > 0 && !rule.allowed_pot_colors.includes(potColor)) {
      return false;
    }
    
    // Check type-specific color rules
    if (potColor && rule.type_color_rules && rule.type_color_rules.length > 0) {
      const typeRule = rule.type_color_rules.find(tcr => tcr.pot_type === potType);
      if (typeRule && typeRule.allowed_colors.length > 0) {
        if (!typeRule.allowed_colors.includes(potColor)) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Check if this would create too many variants (safety limit)
  const colorList = colors.length > 0 ? colors : [null];
  const totalCombinations = potTypes.length * sizes.length * colorList.length;
  if (totalCombinations > 500) {
    return { created: 0, skipped: 0, updated: 0, error: `Too many combinations (${totalCombinations}). Limit is 500 variants.` };
  }

  // Load existing combos to check what needs to be created
  const { data: existing, error: exErr } = await supabase
    .from('product_variants')
    .select('id, pot_type, size, pot_color, price, stock')
    .eq('product_id', productId);
  if (exErr) throw new Error(exErr.message);

  const existingMap = new Map<string, any>();
  for (const e of existing || []) {
    const key = `${e.pot_type}||${e.size}||${e.pot_color ?? ''}`;
    existingMap.set(key, e);
  }

  const toInsert: ProductVariantInsert[] = [];
  const basePrice = Number(product?.price ?? 0);
  const baseStock = Number(product?.stock ?? 0);
  for (const t of potTypes) {
    for (const s of sizes) {
      for (const c of colorList) {
        // Check dependency rules before creating variant
        if (!isCombinationAllowed(t, s, c)) {
          continue; // Skip this combination
        }
        
        const key = `${t}||${s}||${c ?? ''}`;
        const existingVariant = existingMap.get(key);
        
        if (existingVariant) {
          // Keep existing variant - don't reset price/stock
          // Existing variants retain their custom values
        } else {
          // New variant to insert
          toInsert.push({
            product_id: productId,
            pot_type: t,
            size: s,
            pot_color: c ?? null,
            price: basePrice,
            stock: baseStock,
            is_default: false,
          });
        }
      }
    }
  }

  let created = 0;

  // Execute inserts
  if (toInsert.length > 0) {
    // If no existing variants, mark first new as default
    if ((existing?.length ?? 0) === 0) {
      toInsert[0].is_default = true;
    }

    const { data: inserted, error: insErr } = await supabase
      .from('product_variants')
      .insert(toInsert)
      .select('id');
    if (!insErr) {
      created = inserted?.length || 0;
    } else {
      console.error('Insert error:', insErr);
    }
  }

  const skipped = existingMap.size;
  return { created, skipped, updated: 0 };
};
